import { FC, ReactNode, useEffect, useState } from 'react';
import { Form, Select } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/store';
import { ViewerGetDivisionEmployeesResponse } from 'app/services/edpApiV1/viewer';
import { viewerModel } from 'entities/viewer';
import { TDivisionEmployees } from 'shared/types/types';
import { useNotifications } from 'shared/hooks/use-notifications';
import { DefaultOptionType } from 'antd/lib/select';
import { GetEmployeesResponse } from 'app/services/edpApiV1/employees';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { returnErrorOrMessage } from 'shared/helpers/errors';

type TProps = {
  data: ViewerGetDivisionEmployeesResponse | GetEmployeesResponse | undefined;
  label?: string | Element | ReactNode;
  divisionName?: string;
  getEmployees: (params: {
    divisionId?: string;
    page: number;
    size: number;
    search?: string;
    personnelNumber?: string;
    isActive?: boolean;
    allAvailableOrganizations: boolean;
  }) => void;
  setEmployee: React.Dispatch<React.SetStateAction<TDivisionEmployees | null>>;
  onChangeEmployee: () => void;
  rules?: { required: boolean; message?: string }[];
  className?: string;
  defaultUser?: TDivisionEmployees;
  disabled?: boolean;
  formItemName?: string;
};
const marker: { page: number; timeout: NodeJS.Timeout | null } = {
  page: 1,
  timeout: null,
};
const EmployeeInput: FC<TProps> = ({
  data,
  setEmployee,
  divisionName,
  onChangeEmployee,
  getEmployees,
  rules,
  label = 'Сотрудник',
  formItemName = 'employeeId',
  className,
  defaultUser,
  disabled,
}) => {
  const employees: TDivisionEmployees[] = useAppSelector(viewerModel.divisionEmployees.selectors.getEmployees);
  const employeesError = useAppSelector(viewerModel.divisionEmployees.selectors.getEmployeesError);
  const [searchValue, setSearchValue] = useState('');
  const paginationData = data?.pagination;
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const dispatch = useAppDispatch();

  const defaultParams = {
    search: '',
    page: 1,
    size: 10,
    allAvailableOrganizations,
  };

  useEffect(() => {
    if (defaultUser) {
      getEmployees({
        search: `${defaultUser.lastName} ${defaultUser.name} ${defaultUser.middleName}`,
        page: 1,
        size: 10,
        allAvailableOrganizations,
      });
    }
  }, [defaultUser]);

  const resetError = () => {
    dispatch(viewerModel.divisionEmployees.actions.resetError());
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    if (marker.timeout) {
      clearTimeout(marker.timeout);
    }
    marker.timeout = setTimeout(() => {
      marker.page = 1;
      getEmployees({
        search: value,
        page: 1,
        size: 10,
        allAvailableOrganizations,
      });
    }, 500);
  };

  const onFocus = () => {
    getEmployees({
      search: '',
      page: 1,
      size: 10,
      allAvailableOrganizations,
    });
  };
  // ф-ция следит за скролом и отрабатывает как только мы опускаемся до конца списка,
  // летит запрос за новой порцией сотрудников
  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { target } = e;
    if (target instanceof HTMLDivElement) {
      if (target.scrollHeight - target.scrollTop === target.clientHeight) {
        // пока общее колличество сотрудников не привышает того, что находится в стейте делаю запросы
        if (paginationData && paginationData.total > employees?.length) {
          getEmployees({
            search: searchValue,
            page: ++marker.page,
            size: 10,
            allAvailableOrganizations,
          });
        }
      }
    }
  };

  const onSelectEmployee = (id: string, option: DefaultOptionType & { data: TDivisionEmployees }) => {
    setEmployee(option.data);
    onFocus();
    onChangeEmployee();
  };

  const onClearEmployee = () => {
    setSearchValue('');
    onChangeEmployee();
    setEmployee(null);
    getEmployees(defaultParams);
  };

  useEffect(() => {
    getEmployees(defaultParams);
  }, []);

  useNotifications(
    false,
    !!employeesError,
    '',
    returnErrorOrMessage(employeesError, 'Не удалось отобразить сотрудников'),
    resetError,
  );
  return (
    <Form.Item name={formItemName} label={label} extra={divisionName} rules={rules} className={className}>
      <Select
        disabled={disabled}
        showSearch
        placeholder="Поиск сотрудника по ФИО"
        onSelect={onSelectEmployee}
        allowClear
        onClear={onClearEmployee}
        filterOption={false}
        onSearch={handleSearch}
        value={searchValue}
        onFocus={onFocus}
        onPopupScroll={handlePopupScroll}
        options={employees.map(item => ({
          label: `${item.lastName} ${item.name} ${item.middleName ?? ''} / ${item.personnelNumber}`,
          value: item.id,
          data: item,
        }))}
      />
    </Form.Item>
  );
};

export default EmployeeInput;
